import React, { useState, useEffect, useCallback } from 'react';
import { useLoaderData, Link } from 'react-router-dom';
import { Chip } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { Reservation } from '../../types/Reservation';
import { EstudiApiClient } from '../../services/interfaces/EstudiApiClient/EstudiApiClient';
import { formatDate, dateOptions, timeOptions } from '../../utils/functions/formatDate';
import ProfileImage from '../ProfileImage';
import EmailVerificationGate from './EmailVerificationGate';
import ReservationActions from './ReservationActions';
import ReservationHistoryPanel from './ReservationHistoryPanel';
import './ReservationDetail.css';

interface LoaderData {
  reservation: Reservation;
}

type UserRole = 'student' | null;

const statusConfig = {
  confirmed: {
    label: 'Confirmada',
    color: 'success' as const,
    Icon: CheckCircleOutlineIcon
  },
  cancelled: {
    label: 'Cancelada',
    color: 'error' as const,
    Icon: CancelIcon
  },
  in_progress: {
    label: 'Procesando pago',
    color: 'info' as const,
    Icon: HourglassEmptyIcon
  }
};

interface ReservationDetailProps {
  estudiClient: EstudiApiClient;
}

const ReservationDetail: React.FC<ReservationDetailProps> = ({ estudiClient }) => {
  const { reservation } = useLoaderData() as LoaderData;
  const sessionKey = `reservation_verified_${reservation.external_id}`;

  const [verifiedEmail, setVerifiedEmail] = useState<string | null>(() =>
    sessionStorage.getItem(sessionKey)
  );
  const [userRole, setUserRole] = useState<UserRole>(
    () => sessionStorage.getItem(`${sessionKey}_role`) as UserRole
  );
  const [history, setHistory] = useState<Reservation[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  const isClassInFuture = reservation.event.start > new Date();

  const loadPostVerificationData = useCallback(async () => {
    setIsLoadingHistory(true);

    const historyData = await estudiClient.getReservationHistory({
      studentEmail: reservation.student.email,
      teacherId: reservation.teacher.external_id
    });

    setHistory(historyData);
    setIsLoadingHistory(false);
  }, [estudiClient, reservation]);

  useEffect(() => {
    if (verifiedEmail) {
      void loadPostVerificationData();
    }
  }, [verifiedEmail, loadPostVerificationData]);

  const handleEmailVerified = (email: string, role: UserRole) => {
    setVerifiedEmail(email);
    setUserRole(role);
    sessionStorage.setItem(sessionKey, email);
    sessionStorage.setItem(`${sessionKey}_role`, role!);
  };

  const handleRatingSubmit = async (stars: number, comment: string) => {
    await estudiClient.submitReservationRating({
      reservationId: reservation.external_id,
      stars,
      comment
    });
  };

  const status = statusConfig[reservation.status] ?? statusConfig.in_progress;
  const StatusIcon = status.Icon;

  return (
    <div className="reservation-detail">
      <div className="reservation-detail-layout">
        <div className="reservation-detail-main">
          <div className="reservation-info-card">
            <div className="reservation-info-card-header">
              <p className="reservation-info-card-title">Detalle de la clase</p>
              <Chip
                icon={<StatusIcon sx={{ fontSize: '1rem !important' }} />}
                label={status.label}
                color={status.color}
                size="small"
              />
            </div>

            {reservation.course.label && (
              <div className="reservation-detail-row">
                <span className="reservation-detail-label">Curso</span>
                <span className="reservation-detail-value">{reservation.course.label}</span>
              </div>
            )}

            {reservation.course.institution && (
              <div className="reservation-detail-row">
                <span className="reservation-detail-label">Institución</span>
                <span className="reservation-detail-value">
                  {reservation.course.institution.label}
                </span>
              </div>
            )}

            <div className="reservation-detail-row">
              <span className="reservation-detail-label">Profesor</span>
              <span className="reservation-detail-value">
                <div className="teacher-mini-card">
                  <ProfileImage image_url={reservation.teacher.image_url} />
                  <Link
                    to={`/teachers/${reservation.teacher.external_id}/profile`}
                    className="teacher-profile-link">
                    {reservation.teacher.first_name} {reservation.teacher.last_name}
                  </Link>
                </div>
              </span>
            </div>

            <div className="reservation-detail-row">
              <span className="reservation-detail-label">Fecha</span>
              <span className="reservation-detail-value">
                {formatDate(reservation.event.start, dateOptions)}
              </span>
            </div>

            <div className="reservation-detail-row">
              <span className="reservation-detail-label">Horario</span>
              <span className="reservation-detail-value">
                {formatDate(reservation.event.start, timeOptions)} -{' '}
                {formatDate(reservation.event.end, timeOptions)}
              </span>
            </div>

            {verifiedEmail && (
              <React.Fragment>
                <div className="reservation-detail-row">
                  <span className="reservation-detail-label">Ya pagaste</span>
                  <span className="reservation-detail-value">
                    ${reservation.payment_intention.total_amount}
                  </span>
                </div>

                {reservation.payment_intention.total_amount < reservation.total_amount && (
                  <div className="reservation-detail-row">
                    <span className="reservation-detail-label">Falta pagar</span>
                    <span className="reservation-detail-value">
                      ${reservation.total_amount - reservation.payment_intention.total_amount}
                      <span className="reservation-detail-note">
                        {' '}
                        — se abona directamente al profe
                      </span>
                    </span>
                  </div>
                )}
              </React.Fragment>
            )}

            <p className="reservation-id-footer">Reserva #{reservation.external_id}</p>
          </div>

          <ReservationActions
            reservation={reservation}
            isClassInFuture={isClassInFuture}
            telegramLink={reservation.group?.invite_link ?? null}
            existingRate={reservation.rate}
            onRatingSubmit={handleRatingSubmit}
            telegramOnly
          />

          {!verifiedEmail && (
            <EmailVerificationGate reservation={reservation} onVerified={handleEmailVerified} />
          )}

          {verifiedEmail && userRole === 'student' && (
            <ReservationActions
              reservation={reservation}
              isClassInFuture={isClassInFuture}
              telegramLink={reservation.group?.invite_link ?? null}
              existingRate={reservation.rate}
              onRatingSubmit={handleRatingSubmit}
            />
          )}
        </div>

        {verifiedEmail && (
          <div className="reservation-detail-sidebar">
            <ReservationHistoryPanel
              currentReservationId={reservation.external_id}
              history={history}
              isLoading={isLoadingHistory}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ReservationDetail;
